import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { NavLink, useLocation, Outlet, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useAIChat } from '../../context/AIChatContext';
import VoiceVisualizer from '../VoiceVisualizer';
import { AIChatPanel, AIChatToggleButton } from '../AIChatPanel';
import { ThemeQuickToggle } from '../ThemeToggle';
import {
  HomeIcon,
  UsersIcon,
  BuildingOfficeIcon,
  ClipboardDocumentListIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  ChartBarIcon,
  CpuChipIcon,
  DocumentTextIcon,
  ChartPieIcon,
  ArrowTrendingUpIcon,
  EnvelopeIcon,
  MagnifyingGlassIcon,
  FolderIcon,
  RocketLaunchIcon,
  MapPinIcon,
  BeakerIcon,
  ShoppingCartIcon,
  MicrophoneIcon,
  UserGroupIcon,
  BoltIcon,
  LinkIcon,
  PresentationChartLineIcon,
  CubeIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CalendarIcon,
  PhotoIcon,
  VideoCameraIcon,
  ArrowTopRightOnSquareIcon,
  WrenchScrewdriverIcon,
  AdjustmentsHorizontalIcon,
} from '@heroicons/react/24/outline';

const LayoutContainer = styled.div`
  display: flex;
  min-height: 100vh;
`;

const Sidebar = styled.aside`
  width: 250px;
  background: ${({ theme }) => theme?.colors?.cardBackground || 'white'};
  border-right: 1px solid ${({ theme }) => theme?.colors?.border || 'var(--gray-200)'};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, background-color 0.3s ease;
  
  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    z-index: 50;
    transform: ${props => props.isOpen ? 'translateX(0)' : 'translateX(-100%)'};
  }
`;

const SidebarHeader = styled.div`
  padding: 0 1.5rem 8px 1.5rem;
  border-bottom: 1px solid ${({ theme }) => theme?.colors?.border || 'var(--gray-200)'};
  background: var(--primary-600);
  color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  min-height: 3.5rem;
  justify-content: center;
  
  svg {
    width: 100%;
    max-width: 200px;
    height: auto;
  }
  
  h1 {
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 0.25rem;
  }
  
  p {
    font-size: 0.875rem;
    opacity: 0.9;
    margin: 0;
  }
`;

const SidebarNav = styled.nav`
  padding: 1rem 0;
  flex: 1;
`;

const SidebarGroup = styled.div`
  margin-bottom: 1rem;
  
  .group-title {
    padding: 0.5rem 1.5rem;
    font-size: 0.75rem;
    font-weight: 600;
    color: ${({ theme }) => theme?.colors?.textSecondary || 'var(--gray-500)'};
    text-transform: uppercase;
    letter-spacing: 0.05em;
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    transition: all 0.2s;
    border-bottom: 2px solid #ef4444;
    background: ${({ theme }) => theme?.colors?.backgroundSecondary || 'var(--gray-200)'};
    
    &:hover {
      color: ${({ theme }) => theme?.colors?.text || 'var(--gray-700)'}; theme?.colors?.text || 'var(--gray-700)'};
      background-color: ${({ theme }) => theme?.colors?.backgroundTertiary || 'var(--gray-50)'};
    }
    
    svg {
      width: 1rem;
      height: 1rem;
      transition: transform 0.2s;
    }
  }
  
  .group-items {
    overflow: hidden;
    transition: max-height 0.3s ease-in-out, opacity 0.3s ease-in-out;
    
    &.collapsed {
      max-height: 0;
      opacity: 0;
    }
    
    &.expanded {
      max-height: 2000px;
      opacity: 1;
    }
  }
`;

const SubGroup = styled.div`
  margin-left: 0.75rem;
  border-left: 2px solid ${({ theme }) => theme?.colors?.border || 'var(--gray-200)'};
  background-color: ${({ theme }) => theme?.colors?.backgroundTertiary || 'var(--gray-50)'};
  margin-bottom: 0.5rem;
  
  .subgroup-header {
    padding: 0.5rem 1rem;
    font-size: 0.75rem;
    font-weight: 600;
    color: ${({ theme }) => theme?.colors?.textSecondary || 'var(--gray-600)'};
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    transition: all 0.2s;
    background: ${({ theme }) => theme?.colors?.backgroundSecondary || 'var(--gray-100)'};
    border-bottom: 2px solid ${({ theme }) => theme?.colors?.border || 'var(--gray-200)'};
    
    &:hover {
      color: ${({ theme }) => theme?.colors?.text || 'var(--gray-800)'};
      background-color: ${({ theme }) => theme?.colors?.backgroundTertiary || 'var(--gray-100)'};
    }
    
    svg {
      width: 0.875rem;
      height: 0.875rem;
      transition: transform 0.2s;
    }
  }
  
  .subgroup-items {
    overflow: hidden;
    transition: max-height 0.3s ease-in-out, opacity 0.2s ease-in-out;
    
    &.collapsed {
      max-height: 0;
      opacity: 0;
    }
    
    &.expanded {
      max-height: 1000px;
      opacity: 1;
    }
  }
`;

const NavItem = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1.5rem;
  color: ${({ theme }) => theme?.colors?.text || 'var(--gray-700)'};
  text-decoration: none;
  transition: all 0.2s;
  font-weight: 500;
  
  &:hover {
    background-color: ${({ theme }) => theme?.colors?.backgroundTertiary || 'var(--gray-50)'};
    color: var(--primary-600);
  }
  
  &.active {
    background-color: ${({ theme }) => theme?.mode === 'dark' ? 'rgba(239, 68, 68, 0.15)' : 'var(--primary-50)'};
    color: var(--primary-600);
    border-right: 3px solid var(--primary-600);
  }
  
  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`;

const SubNavItem = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 1.25rem;
  padding-left: 2.5rem;
  color: ${({ theme }) => theme?.colors?.text || 'var(--gray-700)'};
  text-decoration: none;
  transition: all 0.2s;
  font-weight: 500;
  font-size: 0.875rem;
  
  &:hover {
    background-color: ${({ theme }) => theme?.colors?.backgroundTertiary || 'var(--gray-50)'};
    color: var(--primary-600);
  }
  
  &.active {
    background-color: ${({ theme }) => theme?.mode === 'dark' ? 'rgba(239, 68, 68, 0.15)' : 'var(--primary-50)'};
    color: var(--primary-600);
    border-right: 3px solid var(--primary-600);
  }
  
  svg {
    width: 1rem;
    height: 1rem;
  }
`;

const SidebarFooter = styled.div`
  padding: 1rem;
  border-top: 1px solid ${({ theme }) => theme?.colors?.border || 'var(--gray-200)'};
`;

const UserInfo = styled.div`
  padding: 0.75rem;
  background-color: ${({ theme }) => theme?.colors?.backgroundTertiary || 'var(--gray-50)'};
  border-radius: 0.5rem;
  margin-bottom: 1rem;
  
  .user-name {
    font-weight: 600;
    color: ${({ theme }) => theme?.colors?.text || 'var(--gray-900)'};
    font-size: 0.875rem;
  }
  
  .user-email {
    color: ${({ theme }) => theme?.colors?.textSecondary || 'var(--gray-600)'};
    font-size: 0.75rem;
  }
  
  .tenant-info {
    color: var(--primary-600);
    font-size: 0.75rem;
    font-weight: 500;
    margin-top: 0.25rem;
  }
`;

const LogoutButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: none;
  border: 1px solid ${({ theme }) => theme?.colors?.border || 'var(--gray-300)'};
  border-radius: 0.5rem;
  color: ${({ theme }) => theme?.colors?.text || 'var(--gray-700)'};
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: ${({ theme }) => theme?.mode === 'dark' ? 'rgba(239, 68, 68, 0.15)' : 'var(--primary-50)'};
    border-color: var(--primary-600);
    color: var(--primary-600);
  }
  
  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`;

const MainContent = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const TopBar = styled.header`
  background: ${({ theme }) => theme?.colors?.headerBackground || 'white'};
  border-bottom: 1px solid ${({ theme }) => theme?.colors?.headerBorder || 'var(--gray-200)'};
  padding: 1rem 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: background-color 0.3s ease, border-color 0.3s ease;
  
  @media (min-width: 769px) {
    .mobile-menu-button {
      display: none;
    }
  }
`;

const PageTitle = styled.h2`
  color: ${({ theme }) => theme?.colors?.text || 'var(--gray-900)'};
  font-size: 1.5rem;
  font-weight: 600;
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: ${({ theme }) => theme?.colors?.textSecondary || 'var(--gray-600)'};
  cursor: pointer;
  padding: 0.5rem;
  
  @media (max-width: 768px) {
    display: block;
  }
  
  svg {
    width: 1.5rem;
    height: 1.5rem;
  }
`;

const ContentArea = styled.div`
  flex: 1;
  padding: 1.5rem;
  overflow-y: auto;
  max-width: 1560px;
  margin: 0 auto;
  width: 100%;
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 40;
  display: ${props => props.show ? 'block' : 'none'};
  
  @media (min-width: 769px) {
    display: none;
  }
`;

const UserAvatar = styled.div`
  width: 2rem;
  height: 2rem;
  background-color: ${({ theme }) => theme?.colors?.primaryLight || 'var(--primary-100)'};
  color: ${({ theme }) => theme?.colors?.primary || 'var(--primary-700)'};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  font-weight: 600;
`;

const UserName = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme?.colors?.text || 'var(--gray-900)'};
`;

const UserEmail = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme?.colors?.textSecondary || 'var(--gray-600)'};
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const HeaderUserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.2;
`;

const HeaderLogoutButton = styled.button`
  background: none;
  border: 1px solid ${({ theme }) => theme?.colors?.border || 'var(--gray-300)'};
  color: ${({ theme }) => theme?.colors?.textSecondary || 'var(--gray-600)'};
  padding: 0.5rem;
  border-radius: 0.375rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  transition: all 0.2s;

  &:hover {
    border-color: ${({ theme }) => theme?.colors?.borderLight || 'var(--gray-400)'};
    color: ${({ theme }) => theme?.colors?.text || 'var(--gray-700)'};
  }

  svg {
    width: 1rem;
    height: 1rem;
  }
`;
const getPageTitle = (pathname) => {
  switch (pathname) {
    case '/dashboard':
      return 'Dashboard';
    case '/users':
      return 'Users';
    case '/companies':
      return 'Companies';
    case '/marketing':
      return 'Marketing Hub';

    case '/ai-command-center':
      return 'AI Command Center';
    case '/ai-actions':
      return 'AI Actions';
    case '/marketing-analytics':
      return 'Marketing Analytics';
    case '/lifecycle':
      return 'Customer Lifecycle';
    case '/tenants':
      return 'Tenants';
    case '/audit-logs':
      return 'Audit Logs';
    case '/settings':
      return 'Settings';
    default:
      if (pathname.includes('/projects')) {
        return 'Projects';
      }
      if (pathname.includes('/campaigns')) {
        return 'Campaigns';
      }
      return 'Marketeer Platform';
  }
};

function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Initialize collapsed groups from localStorage
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    const saved = localStorage.getItem('collapsedMenuGroups');
    return saved ? JSON.parse(saved) : {
      main: false,
      business: false,
      ai: false,
      marketing: false,
      configurators: false,
      advanced: false,
      ecommerce: false,
      system: false,
      admin: false,
    };
  });

  const toggleGroup = (groupKey) => {
    const newCollapsed = {
      ...collapsedGroups,
      [groupKey]: !collapsedGroups[groupKey]
    };
    setCollapsedGroups(newCollapsed);
    localStorage.setItem('collapsedMenuGroups', JSON.stringify(newCollapsed));
  };

  const [collapsedSubGroups, setCollapsedSubGroups] = useState(() => {
    const saved = localStorage.getItem('collapsedMenuSubGroups');
    return saved ? JSON.parse(saved) : {
      campaigns: false,
      content: false,
      deployment: false,
      customers: false,
      analytics: false,
      advancedMarketing: false
    };
  });

  const toggleSubGroup = (subgroupKey) => {
    const newCollapsed = {
      ...collapsedSubGroups,
      [subgroupKey]: !collapsedSubGroups[subgroupKey]
    };
    setCollapsedSubGroups(newCollapsed);
    localStorage.setItem('collapsedMenuSubGroups', JSON.stringify(newCollapsed));
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  // AI Chat context injection based on current route
  const { setCurrentContext, clearContext } = useAIChat();
  const params = useParams();
  
  useEffect(() => {
    // Detect context from URL
    const path = location.pathname;
    
    if (path.includes('/projects/') && params.projectId) {
      setCurrentContext('project', parseInt(params.projectId), `Project ${params.projectId}`);
    } else if (path.includes('/campaigns/') && params.campaignId) {
      setCurrentContext('campaign', parseInt(params.campaignId), `Campaign ${params.campaignId}`);
    } else if (path.includes('/companies/') && params.companyId) {
      setCurrentContext('company', parseInt(params.companyId), `Company ${params.companyId}`);
    } else {
      clearContext();
    }
  }, [location.pathname, params, setCurrentContext, clearContext]);

  return (
    <>
    <LayoutContainer>
      <Overlay show={sidebarOpen} onClick={closeSidebar} />

      <Sidebar isOpen={sidebarOpen}>
        <SidebarHeader>
          <svg width="160" height="32" viewBox="0 0 600 150" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginTop: '0' }}>
            <text
              x="50%"
              y="75%"
              dominantBaseline="middle"
              textAnchor="middle"
              fontFamily="Impact, Haettenschweiler, 'Arial Black', sans-serif"
              fontSize="80"
              fontWeight="bold"
              letterSpacing="5"
              fill="white">
              MARKETEER
            </text>
          </svg>
          <p style={{ margin: '2px 0 3px 0', fontSize: '0.75rem', opacity: '0.9', color: 'white' }}>AI Marketing Platform</p>
        </SidebarHeader>

        <SidebarNav>
          <SidebarGroup>
            <div className="group-title" onClick={() => toggleGroup('main')}>
              <span>Main</span>
              {collapsedGroups.main ? <ChevronRightIcon /> : <ChevronDownIcon />}
            </div>
            <div className={`group-items ${collapsedGroups.main ? 'collapsed' : 'expanded'}`}>
              <NavItem to="/dashboard" onClick={closeSidebar}>
                <HomeIcon />
                Dashboard
              </NavItem>
            </div>
          </SidebarGroup>

          <SidebarGroup>
            <div className="group-title" onClick={() => toggleGroup('business')}>
              <span>Business Structure</span>
              {collapsedGroups.business ? <ChevronRightIcon /> : <ChevronDownIcon />}
            </div>
            <div className={`group-items ${collapsedGroups.business ? 'collapsed' : 'expanded'}`}>
              <NavItem to="/companies" onClick={closeSidebar}>
                <BuildingOfficeIcon />
                Companies
              </NavItem>
            </div>
          </SidebarGroup>

          <SidebarGroup>
            <div className="group-title" onClick={() => toggleGroup('ai')}>
              <span>AI</span>
              {collapsedGroups.ai ? <ChevronRightIcon /> : <ChevronDownIcon />}
            </div>
            <div className={`group-items ${collapsedGroups.ai ? 'collapsed' : 'expanded'}`}>
              <NavItem to="/ai-command-center" onClick={closeSidebar}>
                <CpuChipIcon />
                AI Command Center
              </NavItem>

              <NavItem to="/ai-actions" onClick={closeSidebar}>
                <BoltIcon />
                AI Actions
              </NavItem>

              <NavItem to="/ai-studio/image-generation" onClick={closeSidebar}>
                <PhotoIcon />
                Image Generation
              </NavItem>

              <NavItem to="/ai-studio/video-generation" onClick={closeSidebar}>
                <VideoCameraIcon />
                Video Generation
              </NavItem>
            </div>
          </SidebarGroup>

          <SidebarGroup>
            <div className="group-title" onClick={() => toggleGroup('marketing')}>
              <span>Marketing</span>
              {collapsedGroups.marketing ? <ChevronRightIcon /> : <ChevronDownIcon />}
            </div>
            <div className={`group-items ${collapsedGroups.marketing ? 'collapsed' : 'expanded'}`}>

              {/* Campaigns & Testing Subgroup */}
              <SubGroup>
                <div className="subgroup-header" onClick={() => toggleSubGroup('campaigns')}>
                  <span>🚀 Campaigns & Testing</span>
                  <ChevronRightIcon style={{ transform: collapsedSubGroups.campaigns ? '' : 'rotate(90deg)' }} />
                </div>
                <div className={`subgroup-items ${collapsedSubGroups.campaigns ? 'collapsed' : 'expanded'}`}>
                  <SubNavItem to="/campaigns" onClick={closeSidebar}>
                    <RocketLaunchIcon />
                    Campaigns
                  </SubNavItem>
                  <SubNavItem to="/ab-tests" onClick={closeSidebar}>
                    <BeakerIcon />
                    A/B Testing
                  </SubNavItem>
                  <SubNavItem to="/automation-rules" onClick={closeSidebar}>
                    <BoltIcon />
                    Automation Rules
                  </SubNavItem>
                  <SubNavItem to="/automation" onClick={closeSidebar}>
                    <BoltIcon />
                    Automation Dashboard
                  </SubNavItem>
                </div>
              </SubGroup>

              {/* Content Creation Subgroup */}
              <SubGroup>
                <div className="subgroup-header" onClick={() => toggleSubGroup('content')}>
                  <span>✍️ Content Creation</span>
                  <ChevronRightIcon style={{ transform: collapsedSubGroups.content ? '' : 'rotate(90deg)' }} />
                </div>
                <div className={`subgroup-items ${collapsedSubGroups.content ? 'collapsed' : 'expanded'}`}>
                  <SubNavItem to="/content-management" onClick={closeSidebar}>
                    <DocumentTextIcon />
                    Content Management
                  </SubNavItem>
                  <SubNavItem to="/content-library" onClick={closeSidebar}>
                    <FolderIcon />
                    Content Library
                  </SubNavItem>
                  <SubNavItem to="/blog-automation" onClick={closeSidebar}>
                    <DocumentTextIcon />
                    Blog Automation
                  </SubNavItem>
                  <SubNavItem to="/content-templates" onClick={closeSidebar}>
                    <DocumentTextIcon />
                    Content Templates
                  </SubNavItem>
                  <SubNavItem to="/landing-pages" onClick={closeSidebar}>
                    <DocumentTextIcon />
                    Landing Pages
                  </SubNavItem>
                  <SubNavItem to="/email-marketing" onClick={closeSidebar}>
                    <EnvelopeIcon />
                    Email & Social
                  </SubNavItem>
                  <SubNavItem to="/sms-marketing" onClick={closeSidebar}>
                    <EnvelopeIcon />
                    SMS Marketing
                  </SubNavItem>
                </div>
              </SubGroup>

              {/* Content Deployment Subgroup */}
              <SubGroup>
                <div className="subgroup-header" onClick={() => toggleSubGroup('deployment')}>
                  <span>📤 Content Deployment</span>
                  <ChevronRightIcon style={{ transform: collapsedSubGroups.deployment ? '' : 'rotate(90deg)' }} />
                </div>
                <div className={`subgroup-items ${collapsedSubGroups.deployment ? 'collapsed' : 'expanded'}`}>
                  <SubNavItem to="/deployment-calendar" onClick={closeSidebar}>
                    <CalendarIcon />
                    Deployment Calendar
                  </SubNavItem>
                  <SubNavItem to="/deployment-analytics" onClick={closeSidebar}>
                    <ChartBarIcon />
                    Deployment Analytics
                  </SubNavItem>
                </div>
              </SubGroup>

              {/* Customer Management Subgroup */}
              <SubGroup>
                <div className="subgroup-header" onClick={() => toggleSubGroup('customers')}>
                  <span>👥 Customer Management</span>
                  <ChevronRightIcon style={{ transform: collapsedSubGroups.customers ? '' : 'rotate(90deg)' }} />
                </div>
                <div className={`subgroup-items ${collapsedSubGroups.customers ? 'collapsed' : 'expanded'}`}>
                  <SubNavItem to="/leads" onClick={closeSidebar}>
                    <UserGroupIcon />
                    Leads
                  </SubNavItem>
                  <SubNavItem to="/customers" onClick={closeSidebar}>
                    <UserGroupIcon />
                    Customers
                  </SubNavItem>
                  <SubNavItem to="/lifecycle" onClick={closeSidebar}>
                    <ArrowTrendingUpIcon />
                    Customer Journey
                  </SubNavItem>
                  <SubNavItem to="/vip-program" onClick={closeSidebar}>
                    <UserGroupIcon />
                    VIP Program
                  </SubNavItem>
                </div>
              </SubGroup>

              {/* Analytics & Insights Subgroup */}
              <SubGroup>
                <div className="subgroup-header" onClick={() => toggleSubGroup('analytics')}>
                  <span>📊 Analytics & Insights</span>
                  <ChevronRightIcon style={{ transform: collapsedSubGroups.analytics ? '' : 'rotate(90deg)' }} />
                </div>
                <div className={`subgroup-items ${collapsedSubGroups.analytics ? 'collapsed' : 'expanded'}`}>
                  <SubNavItem to="/marketing" onClick={closeSidebar}>
                    <ChartBarIcon />
                    Marketing Hub
                  </SubNavItem>
                  <SubNavItem to="/marketing-calendar" onClick={closeSidebar}>
                    <CalendarIcon />
                    Marketing Calendar
                  </SubNavItem>
                  <SubNavItem to="/marketing-analytics" onClick={closeSidebar}>
                    <ChartPieIcon />
                    Marketing Analytics
                  </SubNavItem>
                  <SubNavItem to="/seo-audit" onClick={closeSidebar}>
                    <MagnifyingGlassIcon />
                    SEO Audit
                  </SubNavItem>
                  <SubNavItem to="/product-research" onClick={closeSidebar}>
                    <CubeIcon />
                    Product Research
                  </SubNavItem>
                  <SubNavItem to="/ecommerce-analytics" onClick={closeSidebar}>
                    <ShoppingCartIcon />
                    Ecommerce Analytics
                  </SubNavItem>
                </div>
              </SubGroup>

              {/* Advanced Marketing Subgroup */}
              <SubGroup>
                <div className="subgroup-header" onClick={() => toggleSubGroup('advancedMarketing')}>
                  <span>🚀 Advanced Marketing</span>
                  <ChevronRightIcon style={{ transform: collapsedSubGroups.advancedMarketing ? '' : 'rotate(90deg)' }} />
                </div>
                <div className={`subgroup-items ${collapsedSubGroups.advancedMarketing ? 'collapsed' : 'expanded'}`}>
                  <SubNavItem to="/marketing-crew" onClick={closeSidebar}>
                    <CpuChipIcon />
                    AI Marketing Crew
                  </SubNavItem>
                  <SubNavItem to="/event-marketing" onClick={closeSidebar}>
                    <CalendarIcon />
                    Event Marketing
                  </SubNavItem>
                  <SubNavItem to="/paid-ads" onClick={closeSidebar}>
                    <ChartBarIcon />
                    Paid Ads
                  </SubNavItem>
                </div>
              </SubGroup>

            </div>
          </SidebarGroup>

          <SidebarGroup>
            <div className="group-title" onClick={() => toggleGroup('configurators')}>
              <span>Configurators</span>
              {collapsedGroups.configurators ? <ChevronRightIcon /> : <ChevronDownIcon />}
            </div>
            <div className={`group-items ${collapsedGroups.configurators ? 'collapsed' : 'expanded'}`}>
              <NavItem to="/configurators" onClick={closeSidebar}>
                <WrenchScrewdriverIcon />
                All Configurators
              </NavItem>
              <NavItem to="/configurators/orders" onClick={closeSidebar}>
                <ClipboardDocumentListIcon />
                Orders Pipeline
              </NavItem>
              <NavItem to="/configurators/analytics" onClick={closeSidebar}>
                <ChartBarIcon />
                Analytics
              </NavItem>
              <NavItem to="/configurators/pipeline-settings" onClick={closeSidebar}>
                <AdjustmentsHorizontalIcon />
                Pipeline Settings
              </NavItem>
              <NavItem to="/configurators/email-templates" onClick={closeSidebar}>
                <EnvelopeIcon />
                Email Templates
              </NavItem>
              <NavItem to="/configurators/settings" onClick={closeSidebar}>
                <Cog6ToothIcon />
                Settings
              </NavItem>
            </div>
          </SidebarGroup>

          <SidebarGroup>
            <div className="group-title" onClick={() => toggleGroup('advanced')}>
              <span>Advanced Features</span>
              {collapsedGroups.advanced ? <ChevronRightIcon /> : <ChevronDownIcon />}
            </div>
            <div className={`group-items ${collapsedGroups.advanced ? 'collapsed' : 'expanded'}`}>
              <NavItem to="/geo-marketing" onClick={closeSidebar}>
                <MapPinIcon />
                Geographic Marketing
              </NavItem>

              <NavItem to="/sales-signals" onClick={closeSidebar}>
                <PresentationChartLineIcon />
                Sales Signals
              </NavItem>

              <NavItem to="/sales-radar" onClick={closeSidebar}>
                <PresentationChartLineIcon />
                Sales Radar
              </NavItem>

              <NavItem to="/voice-ai" onClick={closeSidebar}>
                <MicrophoneIcon />
                Voice AI
              </NavItem>

              <NavItem to="/marketplace" onClick={closeSidebar}>
                <ShoppingCartIcon />
                Marketplace Manager
              </NavItem>

              <NavItem to="/chat" onClick={closeSidebar}>
                <DocumentTextIcon />
                Chat & Tasks
              </NavItem>
            </div>
          </SidebarGroup>

          <SidebarGroup>
            <div className="group-title" onClick={() => toggleGroup('ecommerce')}>
              <span>E-Commerce</span>
              {collapsedGroups.ecommerce ? <ChevronRightIcon /> : <ChevronDownIcon />}
            </div>
            <div className={`group-items ${collapsedGroups.ecommerce ? 'collapsed' : 'expanded'}`}>
              <NavItem to="/products" onClick={closeSidebar}>
                <CubeIcon />
                Products
              </NavItem>

              <NavItem to="/product-categories" onClick={closeSidebar}>
                <FolderIcon />
                Product Categories
              </NavItem>

              <NavItem to="/vat-rates" onClick={closeSidebar}>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                VAT Rates
              </NavItem>

              <NavItem to="/currencies" onClick={closeSidebar}>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" />
                </svg>
                Currencies
              </NavItem>

              <NavItem to="/countries" onClick={closeSidebar}>
                <MapPinIcon />
                Countries
              </NavItem>

              <NavItem to="/coupons" onClick={closeSidebar}>
                <ShoppingCartIcon />
                Coupons
              </NavItem>

              <NavItem to="/orders" onClick={closeSidebar}>
                <ClipboardDocumentListIcon />
                Orders
              </NavItem>

              <NavItem to="/contracts" onClick={closeSidebar}>
                <DocumentTextIcon />
                Contracts
              </NavItem>
            </div>
          </SidebarGroup>

          <SidebarGroup>
            <div className="group-title" onClick={() => toggleGroup('system')}>
              <span>System</span>
              {collapsedGroups.system ? <ChevronRightIcon /> : <ChevronDownIcon />}
            </div>
            <div className={`group-items ${collapsedGroups.system ? 'collapsed' : 'expanded'}`}>
              <NavItem to="/integrations" onClick={closeSidebar}>
                <LinkIcon />
                Integrations
              </NavItem>
            </div>
          </SidebarGroup>

          <SidebarGroup>
            <div className="group-title" onClick={() => toggleGroup('admin')}>
              <span>Administration</span>
              {collapsedGroups.admin ? <ChevronRightIcon /> : <ChevronDownIcon />}
            </div>
            <div className={`group-items ${collapsedGroups.admin ? 'collapsed' : 'expanded'}`}>
              <NavItem to="/users" onClick={closeSidebar}>
                <UsersIcon />
                Users
              </NavItem>

              {user?.is_super_admin && (
                <NavItem to="/tenants" onClick={closeSidebar}>
                  <BuildingOfficeIcon />
                  Tenants
                </NavItem>
              )}

              <NavItem to="/audit-logs" onClick={closeSidebar}>
                <ClipboardDocumentListIcon />
                Audit Logs
              </NavItem>

              <NavItem to="/settings" onClick={closeSidebar}>
                <Cog6ToothIcon />
                Settings
              </NavItem>

              {user?.is_super_admin && (
                <NavItem 
                  as="a" 
                  href="http://localhost:5678" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  onClick={closeSidebar}
                >
                  <BoltIcon />
                  n8n Workflows
                  <ArrowTopRightOnSquareIcon style={{ width: '14px', marginLeft: 'auto' }} />
                </NavItem>
              )}
            </div>
          </SidebarGroup>
        </SidebarNav>

        <SidebarFooter>
          <UserInfo>
            <div className="user-name">{user?.first_name} {user?.last_name}</div>
            <div className="user-email">{user?.email}</div>
            <div className="tenant-info">
              {user?.tenant?.name} ({user?.tenant?.slug})
            </div>
          </UserInfo>

          <LogoutButton onClick={handleLogout}>
            <ArrowRightOnRectangleIcon />
            Sign out
          </LogoutButton>
        </SidebarFooter>
      </Sidebar>

      <MainContent>
        <TopBar>
          <MobileMenuButton
            className="mobile-menu-button"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            {sidebarOpen ? <XMarkIcon /> : <Bars3Icon />}
          </MobileMenuButton>

          <div></div>

          <HeaderActions>
            <ThemeQuickToggle />
            <VoiceVisualizer />
            {user ? (
              <HeaderUserInfo>
                <UserAvatar>
                  {user.first_name?.charAt(0) || ''}{user.last_name?.charAt(0) || ''}
                </UserAvatar>
                <UserDetails>
                  <UserName>
                    {user.first_name} {user.last_name}
                  </UserName>
                  <UserEmail>
                    {user.email}
                  </UserEmail>
                </UserDetails>
              </HeaderUserInfo>
            ) : (
              <UserEmail>
                Loading user...
              </UserEmail>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              {user?.is_admin && (
                <span className="badge badge-info">Admin</span>
              )}
              {user?.is_super_admin && (
                <span className="badge badge-danger">Super Admin</span>
              )}
            </div>

            <HeaderLogoutButton onClick={handleLogout} title="Sign out">
              <ArrowRightOnRectangleIcon />
            </HeaderLogoutButton>
          </HeaderActions>
        </TopBar>

        <ContentArea>
          <Outlet />
        </ContentArea>
      </MainContent>
    </LayoutContainer>
    
    {/* AI Chat Panel and Toggle */}
    <AIChatPanel />
    <AIChatToggleButton />
    </>
  );
}

export default Layout;